import type { AwardSplitPayload, OutreachDraftPayload } from '../types/robbieProposal';

/**
 * How a Robbie card the buyer applied reaches the screen it changes.
 *
 * The apply itself runs where the card is (the chat rail) and saves through
 * the step's own endpoint. The step on screen holds its own working copy —
 * typed answers, ticks, a composer draft, award sliders — so it is told what
 * changed and updates that copy at once, the way step 2 re-reads the RFQ.
 *
 * A card can be applied from history while its step is not mounted. Such an
 * event is held, and handed to the first subscriber of its kind that mounts,
 * so a draft applied on step 3 is in the step-4 composer when the buyer gets
 * there. Held events are delivered once.
 */
export type RobbieAppliedEvent =
  | { kind: 'context_answers'; bomId: string; answers: Record<string, string> }
  | { kind: 'validation_actions'; bomId: string }
  | { kind: 'shortlist'; bomId: string; lists: Array<{ componentId: string; componentKey: string; suppliers: string[] }> }
  | ({ kind: 'outreach_draft' } & OutreachDraftPayload)
  | ({ kind: 'award_split' } & AwardSplitPayload);

export type RobbieAppliedKind = RobbieAppliedEvent['kind'];
type Handler<K extends RobbieAppliedKind> = (event: Extract<RobbieAppliedEvent, { kind: K }>) => void;

const MAX_HELD = 10;

export interface RobbieAppliedBus {
  publish: (event: RobbieAppliedEvent) => void;
  subscribe: <K extends RobbieAppliedKind>(kind: K, handler: Handler<K>) => () => void;
}

export const createRobbieAppliedBus = (): RobbieAppliedBus => {
  const handlers = new Map<RobbieAppliedKind, Set<(event: RobbieAppliedEvent) => void>>();
  let held: RobbieAppliedEvent[] = [];

  return {
    publish: (event) => {
      const listening = handlers.get(event.kind);
      if (listening && listening.size > 0) {
        listening.forEach((h) => h(event));
        return;
      }
      held = [...held, event].slice(-MAX_HELD);
    },
    subscribe: (kind, handler) => {
      // Events are filed by kind, so a handler only ever receives its own.
      const untyped = handler as (event: RobbieAppliedEvent) => void;
      const set = handlers.get(kind) ?? new Set();
      set.add(untyped);
      handlers.set(kind, set);
      const mine = held.filter((e) => e.kind === kind);
      if (mine.length > 0) {
        held = held.filter((e) => e.kind !== kind);
        mine.forEach((e) => untyped(e));
      }
      return () => {
        set.delete(untyped);
      };
    },
  };
};
